import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material'
import { FC, useMemo } from 'react'
import { IgnoredActionType, ModerationEvent } from '../../../../api/models'
import { AUTO_TYPE_LABEL, BadgeStatus, ModerationEventStatus, UNKNOWN_ID, UNKNOWN_NAME, UNKNOWN_TYPE } from '../cameraOverviewEnums'
import { CameraHistoryItemDetails } from './CameraHistoryItemDetails'
import { formatDocumentedByName, getEscalationLevel, getEventTypeInfo, getInfractionTypeLabel } from '../cameraOverviewUtils'
import { useLocationCameras } from '../../../../api/apiQueries'
import { getDisplayName } from '../../../../utils/cameraUtils'

interface CameraHistoryItemProps {
  event: ModerationEvent
  expanded?: boolean
  onToggle: (isExpanded: boolean) => void
}

export const CameraHistoryItem: FC<CameraHistoryItemProps> = ({ event, expanded = false, onToggle }) => {
  const { data: cameras = [] } = useLocationCameras(event.locationId)

  const eventTypeInfo = useMemo(() => getEventTypeInfo(event), [event])

  const documentedBy = useMemo(() => {
    if (eventTypeInfo.isAutomated && 'documentedBy' in event.details) {
      return event.details.documentedBy ?? UNKNOWN_NAME
    }
    return formatDocumentedByName(event.createdBy) ?? UNKNOWN_NAME
  }, [event, eventTypeInfo.isAutomated])

  const getStatusBgColor = () => {
    switch (event.status) {
      case ModerationEventStatus.Active:
        return 'var(--shape-confirm-transparent)'
      case ModerationEventStatus.Completed:
        return 'var(--shape-confirm)'
      case ModerationEventStatus.Failed:
        return 'var(--shape-error)'
      default:
        return getEscalationLevel(event) === BadgeStatus.Unenrolled ? 'var(--shape-error)' : 'var(--shape-confirm)'
    }
  }

  const getFirstTypeTagBgColor = () => {
    if (eventTypeInfo.isIgnored) {
      return 'var(--neutral-500)'
    }
    if (eventTypeInfo.enforcementLevelLabel === BadgeStatus.Unenrolled || eventTypeInfo.manualEventLabel === BadgeStatus.Unenrolled) {
      return 'var(--red-500)'
    }
    if (eventTypeInfo.enforcementLevelLabel?.includes(BadgeStatus.Warned) || eventTypeInfo.manualEventLabel?.includes(BadgeStatus.Warned)) {
      return 'var(--yellow-400)'
    }
    return 'var(--shape-confirm)'
  }

  const getSecondTypeBgColor = (label: string) => {
    if (label === BadgeStatus.Unenrolled) return 'var(--shape-error-var)'
    if (label.includes(BadgeStatus.Warned)) return 'var(--yellow-500)'
    return 'var(--shape-confirm)'
  }

  const getStatusTextColor = (status: string) => {
    switch (status) {
      case ModerationEventStatus.Completed:
      case ModerationEventStatus.Failed:
        return 'var(--text-inverted)'
      default:
        return 'var(--text-primary)'
    }
  }

  const device = useMemo(() => {
    if (event.details && 'device' in event.details) {
      return event.details.device
    } else {
      return undefined
    }
  }, [event])

  const deviceName = useMemo(() => {
    return device ? device.name : UNKNOWN_NAME
  }, [device])

  const deviceType = useMemo(() => {
    if (!device) {
      return UNKNOWN_TYPE
    }
    const matchedCamera = cameras.find(camera => camera.uuid === device.id)

    if (!matchedCamera) {
      return UNKNOWN_TYPE
    }

    return getDisplayName(matchedCamera.model) ?? UNKNOWN_TYPE
  }, [device, cameras])

  const deviceId = useMemo(() => {
    return device ? device.id : UNKNOWN_ID
  }, [device])

  const statusText = event.status

  const reason = getInfractionTypeLabel(event)

  return (
    <Accordion
      variant="outlined"
      sx={{ borderRadius: 'var(--border-radius-l)', width: '956px' }}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(isExpanded)}
      data-testid={`camera-history-item-${event.id}`}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-content"
        id="panel-header"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: 2,
          paddingY: 'var(--spacing-s)'
        }}
      >
        <Box sx={{ flexShrink: 0, width: '15.625rem' }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: 'var(--size-s)', fontWeight: 500, whiteSpace: 'nowrap' }}
            data-testid={`camera-name-${event.id}`}
          >
            {deviceName}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: 'var(--size-s)', fontWeight: 500, color: 'var(--button-disabled-fg)', whiteSpace: 'nowrap' }}
            data-testid={`camera-type-${event.id}`}
          >
            {deviceType}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 'var(--size-2xs)', fontWeight: 400, whiteSpace: 'nowrap' }}
            data-testid={`camera-id-${event.id}`}
          >
            {deviceId}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '9rem', flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Type
          </Typography>
          {eventTypeInfo.isAutomated ? (
            <Box sx={{ display: 'flex', height: '2rem', mt: 0.5 }} data-testid={`history-item-type-${event.id}`}>
              <Box
                sx={{
                  backgroundColor: getFirstTypeTagBgColor(),
                  borderRadius: 'var(--border-radius-m) 0 0 var(--border-radius-m)',
                  px: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography sx={{ color: 'var(--text-inverted)', fontSize: 'var(--size-s)', fontWeight: 400, whiteSpace: 'nowrap' }}>
                  {AUTO_TYPE_LABEL}
                </Typography>
              </Box>
              {eventTypeInfo.isIgnored && (
                <Box
                  sx={{
                    backgroundColor: 'var(--neutral-700)',
                    borderRadius: '0 var(--border-radius-m) var(--border-radius-m) 0',
                    px: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography sx={{ color: 'var(--text-inverted)', fontSize: 'var(--size-s)', fontWeight: 400, whiteSpace: 'nowrap' }}>
                    {IgnoredActionType.Ignored}
                  </Typography>
                </Box>
              )}
              {!eventTypeInfo.isIgnored && eventTypeInfo.enforcementLevelLabel && (
                <Box
                  sx={{
                    backgroundColor: getSecondTypeBgColor(eventTypeInfo.enforcementLevelLabel),
                    borderRadius: '0 var(--border-radius-m) var(--border-radius-m) 0',
                    px: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography sx={{ color: 'var(--text-inverted)', fontSize: 'var(--size-s)', fontWeight: 400, whiteSpace: 'nowrap' }}>
                    {eventTypeInfo.enforcementLevelLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', height: '2rem', mt: 0.5 }} data-testid={`history-item-type-${event.id}`}>
              {eventTypeInfo.manualEventLabel && (
                <Box
                  sx={{
                    backgroundColor: getFirstTypeTagBgColor(),
                    borderRadius: 'var(--border-radius-m)',
                    px: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography sx={{ color: 'var(--text-inverted)', fontSize: 'var(--size-s)', fontWeight: 400, whiteSpace: 'nowrap' }}>
                    {eventTypeInfo.manualEventLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '8rem', flexShrink: 0, ml: 4 }}>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Box
            sx={{
              backgroundColor: getStatusBgColor(),
              borderRadius: 'var(--border-radius-m)',
              px: 1,
              height: '2rem',
              display: 'flex',
              alignItems: 'center',
              mt: 0.5
            }}
            data-testid={`history-item-status-${event.id}`}
          >
            <Typography sx={{ color: getStatusTextColor(statusText), fontSize: 'var(--size-s)', fontWeight: 400, whiteSpace: 'nowrap' }}>
              {statusText}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '8.5rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="caption" color="text.secondary">
            Reason
          </Typography>
          <Typography
            variant="body2"
            sx={{ display: 'flex', alignItems: 'center', minHeight: '2rem', fontSize: 'var(--size-s)' }}
            data-testid={`history-item-reason-${event.id}`}
          >
            {reason}
          </Typography>
        </Box>

        <Box sx={{ width: '8.5rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 'var(--spacing-s)' }}>
          <Typography variant="caption" color="text.secondary">
            Documented by
          </Typography>
          <Typography
            variant="body2"
            sx={{ wordBreak: 'break-word', display: 'flex', alignItems: 'center', minHeight: '2rem', fontSize: 'var(--size-s)' }}
            data-testid={`history-item-documented-by-${event.id}`}
          >
            {documentedBy}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <CameraHistoryItemDetails event={event} />
      </AccordionDetails>
    </Accordion>
  )
}
